import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    MenuItem,
    Radio,
    RadioGroup,
    TextField,
    Typography,
    useMediaQuery,
} from "@material-ui/core"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import { Add, Delete, ExpandMore, LocalOffer, Save } from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import React, { useEffect } from "react"
import type { CupomItem } from "../models/cupomEstabelecimentoFornecedorModel"
import { CupomEstabelecimentoFornecedorActionsTypes } from "../context/cupomEstabelecimentoFornecedorActions"
import { useCupomEstabelecimentoFornecedorContext } from "../context/useCupomEstabelecimentoFornecedorContext"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: { paddingLeft: "20px", paddingRight: "20px", paddingTop: "80px" },
        [theme.breakpoints.down("sm")]: { paddingLeft: "15px", paddingRight: "15px", paddingTop: "70px" },
    },
    content: {
        margin: "auto",
        maxWidth: "900px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    header: {
        marginBottom: theme.spacing(4),
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
            alignItems: "flex-start",
            gap: theme.spacing(1),
            marginBottom: theme.spacing(3),
        },
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    addButton: {
        borderRadius: 20,
        backgroundColor: "#4caf50",
        color: "white",
        fontWeight: 600,
        boxShadow: "0 2px 8px rgba(76,175,80,0.3)",
        "&:hover": { backgroundColor: "#45a049" },
        [theme.breakpoints.down("sm")]: { fontSize: "0.75rem" },
    },
    saveButton: {
        borderRadius: 25,
        backgroundColor: "#4caf50",
        color: "white",
        padding: theme.spacing(1.5, 4),
        fontWeight: 600,
        boxShadow: "0 4px 15px rgba(76,175,80,0.3)",
        minWidth: "180px",
        "&:hover": {
            backgroundColor: "#45a049",
            transform: "translateY(-2px)",
            boxShadow: "0 6px 20px rgba(76,175,80,0.4)",
        },
        [theme.breakpoints.down("sm")]: { minWidth: "100%", padding: theme.spacing(2, 3) },
    },
    deleteButton: {
        borderRadius: 20,
        borderColor: "#e74c3c",
        color: "#e74c3c",
        "&:hover": { backgroundColor: "rgba(231,76,60,0.08)" },
    },
    buttonContainer: {
        display: "flex",
        justifyContent: "center",
        marginTop: theme.spacing(4),
        [theme.breakpoints.down("sm")]: { marginTop: theme.spacing(3) },
    },
    accordion: {
        borderRadius: "20px !important",
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        marginBottom: theme.spacing(2),
        "&:before": { display: "none" },
        [theme.breakpoints.down("sm")]: { borderRadius: "15px !important" },
    },
    accordionSummary: {
        backgroundColor: "#f8f9fa",
        borderRadius: "20px 20px 0 0",
        borderBottom: "2px solid #e9ecef",
        "&.Mui-expanded": { minHeight: 48 },
    },
    summaryTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        flexGrow: 1,
    },
    utilizadoChip: {
        fontSize: "0.75rem",
        color: "#7f8c8d",
        marginLeft: theme.spacing(1),
    },
    sectionLabel: {
        fontWeight: 600,
        color: "#2c3e50",
        marginBottom: theme.spacing(1),
        marginTop: theme.spacing(2),
    },
    textField: {
        "& .MuiOutlinedInput-root": {
            borderRadius: 8,
            "& fieldset": { borderColor: "#e0e0e0" },
            "&:hover fieldset": { borderColor: "#4caf50" },
            "&.Mui-focused fieldset": { borderColor: "#4caf50" },
        },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    emptyText: {
        textAlign: "center",
        color: "#7f8c8d",
        padding: theme.spacing(4),
    },
}))

function CampoTexto({
    label,
    value,
    onChange,
    placeholder,
    type = "text",
    disabled,
}: {
    label: string
    value: string | number | null
    onChange: (v: string) => void
    placeholder?: string
    type?: string
    disabled?: boolean
}) {
    const classes = useStyles()
    return (
        <TextField
            className={classes.textField}
            label={label}
            variant="outlined"
            size="small"
            fullWidth
            type={type}
            placeholder={placeholder}
            value={value ?? ""}
            onChange={e => {
                const v = e.target.value
                onChange(v)
            }}
            disabled={disabled}
        />
    )
}

function CupomCard({
    cupom,
    disabled,
}: {
    cupom: CupomItem
    disabled: boolean
}) {
    const classes = useStyles()
    const { dispatch } = useCupomEstabelecimentoFornecedorContext()

    const atualizar = (campo: keyof CupomItem, valor: string | number | boolean | null) => {
        dispatch({
            type: CupomEstabelecimentoFornecedorActionsTypes.ATUALIZAR_CAMPO,
            payload: { localId: cupom.localId, campo, valor },
        })
    }

    const remover = () => {
        dispatch({
            type: CupomEstabelecimentoFornecedorActionsTypes.REMOVER,
            payload: { localId: cupom.localId, dbId: cupom.id },
        })
    }

    const fimCupomFormatado = cupom.fimCupomTimestamp
        ? new Date(cupom.fimCupomTimestamp).toISOString().split("T")[0]
        : ""

    return (
        <Accordion className={classes.accordion} defaultExpanded={cupom.id === null}>
            <AccordionSummary expandIcon={<ExpandMore />} className={classes.accordionSummary}>
                <Typography className={classes.summaryTitle}>
                    {cupom.nomeCupom || "Novo Cupom"}
                </Typography>
                <Typography className={classes.utilizadoChip}>
                    {cupom.quantidadeUtilizada > 0 ? `Usado ${cupom.quantidadeUtilizada}x` : ""}
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container spacing={2} style={{ width: "100%" }}>
                    {/* Linha 1: código, qtd por cliente, qtd máxima */}
                    <Grid item xs={12} sm={4}>
                        <CampoTexto
                            label="Código do Cupom"
                            value={cupom.nomeCupom}
                            onChange={v => atualizar("nomeCupom", v.toUpperCase().replace(/\s/g, ""))}
                            placeholder="CUPOMSEMNOME"
                            disabled={disabled}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <CampoTexto
                            label="Qtd por Cliente"
                            value={cupom.quantidadePorCliente}
                            onChange={v => atualizar("quantidadePorCliente", v !== "" ? Number(v) : 1)}
                            type="number"
                            disabled={disabled}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <CampoTexto
                            label="Qtd Máxima (opcional)"
                            value={cupom.quantidadeMaxima}
                            onChange={v => atualizar("quantidadeMaxima", v !== "" ? Number(v) : null)}
                            type="number"
                            disabled={disabled}
                        />
                    </Grid>

                    {/* Linha 2: valor, valor mínimo pedido, máximo desconto */}
                    <Grid item xs={12} sm={4}>
                        <CampoTexto
                            label="Valor do Cupom"
                            value={cupom.valor}
                            onChange={v => atualizar("valor", v !== "" ? Number(v.replace(",", ".")) : 0)}
                            type="number"
                            disabled={disabled}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <CampoTexto
                            label="Valor Mínimo do Pedido (opcional)"
                            value={cupom.valorMinimoPedido}
                            onChange={v => atualizar("valorMinimoPedido", v !== "" ? Number(v.replace(",", ".")) : null)}
                            type="number"
                            disabled={disabled}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <CampoTexto
                            label="Máximo Desconto (opcional)"
                            value={cupom.maximoDesconto}
                            onChange={v => atualizar("maximoDesconto", v !== "" ? Number(v.replace(",", ".")) : null)}
                            type="number"
                            disabled={disabled}
                        />
                    </Grid>

                    {/* Linha 3: tipo, válido para, forma de aplicação */}
                    <Grid item xs={12} sm={4}>
                        <TextField
                            className={classes.textField}
                            select
                            label="Tipo do Cupom"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={cupom.percentual ? "true" : "false"}
                            onChange={e => {
                                const v = e.target.value
                                atualizar("percentual", v === "true")
                            }}
                            disabled={disabled}
                        >
                            <MenuItem value="false">Valor Fixo (R$)</MenuItem>
                            <MenuItem value="true">Percentual (%)</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            className={classes.textField}
                            select
                            label="Válido para"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={cupom.apenasPrimeiraCompra ? "true" : "false"}
                            onChange={e => {
                                const v = e.target.value
                                atualizar("apenasPrimeiraCompra", v === "true")
                            }}
                            disabled={disabled}
                        >
                            <MenuItem value="false">Todas as compras</MenuItem>
                            <MenuItem value="true">Primeira Compra</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            className={classes.textField}
                            select
                            label="Forma de Aplicação"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={cupom.automatico ? "true" : "false"}
                            onChange={e => {
                                const v = e.target.value
                                atualizar("automatico", v === "true")
                            }}
                            disabled={disabled}
                        >
                            <MenuItem value="false">Digitada</MenuItem>
                            <MenuItem value="true">Automática</MenuItem>
                        </TextField>
                    </Grid>

                    {/* Linha 4: exigir facebook, endereço único, cpf */}
                    <Grid item xs={12} sm={4}>
                        <TextField
                            className={classes.textField}
                            select
                            label="Exigir Facebook"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={cupom.exigirFacebookParaDesconto ? "true" : "false"}
                            onChange={e => {
                                const v = e.target.value
                                atualizar("exigirFacebookParaDesconto", v === "true")
                            }}
                            disabled={disabled}
                        >
                            <MenuItem value="false">Não</MenuItem>
                            <MenuItem value="true">Sim</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            className={classes.textField}
                            select
                            label="Exigir Endereço Único"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={cupom.exigirEnderecoUnicoParaDesconto ? "true" : "false"}
                            onChange={e => {
                                const v = e.target.value
                                atualizar("exigirEnderecoUnicoParaDesconto", v === "true")
                            }}
                            disabled={disabled}
                        >
                            <MenuItem value="false">Não</MenuItem>
                            <MenuItem value="true">Sim</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            className={classes.textField}
                            select
                            label="Exigir CPF"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={cupom.exigirCpfParaDesconto ? "true" : "false"}
                            onChange={e => {
                                const v = e.target.value
                                atualizar("exigirCpfParaDesconto", v === "true")
                            }}
                            disabled={disabled}
                        >
                            <MenuItem value="false">Não</MenuItem>
                            <MenuItem value="true">Sim</MenuItem>
                        </TextField>
                    </Grid>

                    {/* Linha 5: horário almoço, frete grátis, somente app/site */}
                    <Grid item xs={12} sm={4}>
                        <TextField
                            className={classes.textField}
                            select
                            label="Somente Horário de Almoço?"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={cupom.horarioAlmoco ? "true" : "false"}
                            onChange={e => {
                                const v = e.target.value
                                atualizar("horarioAlmoco", v === "true")
                            }}
                            disabled={disabled}
                        >
                            <MenuItem value="false">Não</MenuItem>
                            <MenuItem value="true">Sim</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            className={classes.textField}
                            select
                            label="Frete Grátis?"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={cupom.freteGratis ? "true" : "false"}
                            onChange={e => {
                                const v = e.target.value
                                atualizar("freteGratis", v === "true")
                            }}
                            disabled={disabled}
                        >
                            <MenuItem value="false">Não</MenuItem>
                            <MenuItem value="true">Sim</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            className={classes.textField}
                            select
                            label="Disponível em"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={cupom.appSiteTodos}
                            onChange={e => {
                                const v = Number(e.target.value)
                                atualizar("appSiteTodos", v)
                            }}
                            disabled={disabled}
                        >
                            <MenuItem value={0}>App e Site</MenuItem>
                            <MenuItem value={1}>Somente App</MenuItem>
                            <MenuItem value={2}>Somente Site</MenuItem>
                        </TextField>
                    </Grid>

                    {/* Telefone cliente */}
                    <Grid item xs={12} sm={6}>
                        <CampoTexto
                            label="Telefone do Cliente (opcional)"
                            value={cupom.telefoneCliente}
                            onChange={v => atualizar("telefoneCliente", v || null)}
                            placeholder="xx xxxxx-xxxx"
                            disabled={disabled}
                        />
                    </Grid>

                    {/* Validade */}
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" className={classes.sectionLabel}>
                            Validade
                        </Typography>
                        <RadioGroup
                            row
                            value={cupom.validadeDias !== null ? String(cupom.validadeDias) : ""}
                            onChange={e => {
                                const v = e.target.value
                                atualizar("validadeDias", v !== "" ? Number(v) : null)
                            }}
                        >
                            <FormControlLabel value="1" control={<Radio size="small" disabled={disabled} />} label="1 dia" />
                            <FormControlLabel value="7" control={<Radio size="small" disabled={disabled} />} label="7 dias" />
                            <FormControlLabel value="30" control={<Radio size="small" disabled={disabled} />} label="30 dias" />
                            <FormControlLabel value="3650" control={<Radio size="small" disabled={disabled} />} label="Indeterminado" />
                            <FormControlLabel value="0" control={<Radio size="small" disabled={disabled} />} label="Personalizado" />
                        </RadioGroup>
                    </Grid>

                    {/* Data fim (somente quando personalizado) */}
                    {cupom.validadeDias === 0 && (
                        <Grid item xs={12} sm={6}>
                            <TextField
                                className={classes.textField}
                                label="Data Fim"
                                variant="outlined"
                                size="small"
                                fullWidth
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                value={fimCupomFormatado}
                                onChange={e => {
                                    const dateStr = e.target.value
                                    const ts = dateStr ? new Date(dateStr).getTime() : null
                                    atualizar("fimCupomTimestamp", ts)
                                }}
                                disabled={disabled}
                            />
                        </Grid>
                    )}

                    {/* Checkboxes */}
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={cupom.visivelTelaInicial}
                                    onChange={e => {
                                        const checked = e.target.checked
                                        atualizar("visivelTelaInicial", checked)
                                    }}
                                    size="small"
                                    disabled={disabled}
                                />
                            }
                            label="Visível na tela inicial"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={cupom.olharListaClientes}
                                    onChange={e => {
                                        const checked = e.target.checked
                                        atualizar("olharListaClientes", checked)
                                    }}
                                    size="small"
                                    disabled={disabled}
                                />
                            }
                            label="Aplicar apenas a clientes definidos"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={cupom.ativo}
                                    onChange={e => {
                                        const checked = e.target.checked
                                        atualizar("ativo", checked)
                                    }}
                                    size="small"
                                    disabled={disabled}
                                />
                            }
                            label="Ativo"
                        />
                    </Grid>

                    {/* Descrição / regulamento */}
                    <Grid item xs={12}>
                        <TextField
                            className={classes.textField}
                            label="Regulamento / Descrição (opcional)"
                            variant="outlined"
                            size="small"
                            fullWidth
                            multiline
                            rows={2}
                            value={cupom.descricao}
                            onChange={e => {
                                const v = e.target.value
                                atualizar("descricao", v)
                            }}
                            disabled={disabled}
                        />
                    </Grid>

                    {/* Botão excluir */}
                    <Grid item xs={12} style={{ textAlign: "right" }}>
                        <Button
                            variant="outlined"
                            className={classes.deleteButton}
                            startIcon={<Delete />}
                            size="small"
                            onClick={remover}
                            disabled={disabled}
                        >
                            Excluir Cupom
                        </Button>
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    )
}

export function TabelaCupons() {
    const classes = useStyles()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
    const { dados, dispatch, salvar, erroCarregamento } = useCupomEstabelecimentoFornecedorContext()
    const { enqueueSnackbar } = useSnackbar()

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    useEffect(() => {
        if (erroCarregamento) alertStart(erroCarregamento, "error")
    }, [erroCarregamento])

    const handleAdicionar = () => {
        dispatch({ type: CupomEstabelecimentoFornecedorActionsTypes.ADICIONAR })
    }

    const handleSalvar = async () => {
        for (const c of dados.cupons) {
            if (!c.nomeCupom.trim()) {
                alertStart("O código do cupom não pode estar vazio.", "error")
                return
            }
            if (c.valor <= 0) {
                alertStart("O valor do cupom deve ser maior que zero.", "error")
                return
            }
            if (c.validadeDias === 0 && !c.fimCupomTimestamp) {
                alertStart("Informe a data de validade para o cupom personalizado.", "error")
                return
            }
        }
        try {
            await salvar()
            alertStart("Cupons salvos com sucesso!", "success")
        } catch (error) {
            alertStart((error as Error).message || "Erro ao salvar cupons.", "error")
        }
    }

    return (
        <div className={classes.container}>
            <div className={classes.content}>
                <Box className={classes.header}>
                    <Box>
                        <Typography variant="h4" className={classes.titleText}>
                            Cupons de Desconto
                        </Typography>
                        <Typography variant="subtitle1" style={{ color: "#7f8c8d" }}>
                            Cadastre cupons de desconto para incentivar os seus clientes
                        </Typography>
                    </Box>
                    <Button
                        className={classes.addButton}
                        startIcon={<Add />}
                        size="small"
                        onClick={handleAdicionar}
                        disabled={!!erroCarregamento}
                    >
                        {isMobile ? "Novo" : "Novo Cupom"}
                    </Button>
                </Box>

                {dados.cupons.length === 0 ? (
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        style={{
                            borderRadius: 20,
                            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                            backgroundColor: "white",
                            padding: "48px 24px",
                        }}
                    >
                        <LocalOffer style={{ fontSize: 48, color: "#bdc3c7", marginBottom: 16 }} />
                        <Typography className={classes.emptyText}>
                            Nenhum cupom cadastrado. Clique em &quot;Novo Cupom&quot; para adicionar.
                        </Typography>
                    </Box>
                ) : (
                    dados.cupons.map(cupom => (
                        <CupomCard key={cupom.localId} cupom={cupom} disabled={!!erroCarregamento} />
                    ))
                )}

                <Box className={classes.buttonContainer}>
                    <Button
                        className={classes.saveButton}
                        startIcon={!isMobile && <Save />}
                        onClick={handleSalvar}
                        disabled={!!erroCarregamento || dados.cupons.length === 0}
                        size={isMobile ? "large" : "medium"}
                    >
                        Salvar Cupons
                    </Button>
                </Box>
            </div>
        </div>
    )
}
